import { FlashList } from '@shopify/flash-list';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshControl, TouchableOpacity, View, Platform } from 'react-native';

import { AvatarProps } from '@components/Avatar/Avatar';
import { EmailAvatar } from '@components/Avatar/EmailAvatar';
import EmptyStateNoSearch from '@components/EmptyState/EmptyStateNoSearch';
import Highlighter from '@components/HighlightWords/index';
import { ContactType } from '@components/Invite/Contact';
import ContactV3 from '@components/Invite/ContactV3';
import { WebHoverBox } from '@components/Modals/WebHoverBox';
import RadioButton from '@components/RadioButton/RadioButton';
import { Box, Text } from '@components/Restyle';
import ActivityIndicatorLoading from '@components/shared/ActivityIndicatorLoading';
import { MarginBottomListFooter } from '@components/shared/ListSpacers';
import { ContactRowSkeleton } from '@components/shared/skeletons/ContactRowSkeleton';
import { SkeletonList } from '@components/shared/skeletons/SkeletonList';
import { Group } from '@graphql/generated';
import { useContactListSpacers } from '@hooks/useContactListSpacers';
import theme from '@themes/theme';
import { convertUsersToContacts } from '@utils/convertUsersToContacts';
import { getSuggestionEmail } from '@utils/emailSuggestion';

export interface ContactListProps {
  contacts: ContactType[];
  taskTagUsers?: ContactType[];
  onPress: (item: ContactType) => void;
  onPressGroup?: (group: Group, isAdd: boolean) => void;
  onPressEmailAddress?: (email: string) => void;
  filterVal?: string;
  alphabetize?: boolean;
  headerSpace?: keyof typeof theme.spacing;
  footerSpace?: number;
  isEditing?: boolean;
  isInviting?: boolean;
  loading?: boolean;
  selected?: string[];
  shouldDisplayPhoneContactLabel?: boolean;
  showMessageIcon?: boolean;
  onMessagePress?: (item: ContactType) => void;
  showEmptyStateNoSearch?: boolean;
  disabledUsersId?: string[];
  refreshing?: boolean;
  onRefresh?: () => void;
  hasNextPage?: boolean;
  loadMore?: () => void;
  avatarSize?: AvatarProps['size'];
  groupList?: Group[];
  invitedList?: string[]; // for save Email list has invited in chat group
  alreadyInvitedUserIds?: string[]; // TaskTag user IDs with pending userRequests in the chat
  /** TA-10279: opt-in — swaps the loading spinner for 3 skeleton rows.
   * Defaults to false so existing (native) callers keep the pre-existing
   * spinner; web callers adopting the TA-10089 skeleton pattern pass true. */
  showLoadingSkeleton?: boolean;
}

// ContactV3 row: py='xs' (8+8) + max(40px medium avatar, 21px name + 20px
// secondary line) = 57px for the common name+email row (56 name-only, ~81
// with a role label). FlashList replaces this with a running average once
// rows are measured, so it only shapes the first render.
const ESTIMATED_CONTACT_ITEM_SIZE = 57;

// TA-11104: both FlashLists below use a FIXED large drawDistance (FlashList
// reads it once at mount). On iOS a row that FlashList reuses can land at the
// wrong position (see FlashListPlainViewCell for the cause), which blanked
// this list on a fast scroll back up at the 250px default. 10000px keeps
// every row mounted, so nothing is reused, up to about 180 contacts (10000px
// plus the viewport, at ~57px per row). A longer list can still hit the bug
// until FlashList itself is fixed.
const CONTACT_LIST_DRAW_DISTANCE = 10000;

const renderContactRowSkeleton = () => <ContactRowSkeleton />;

type SuggestionEmailType = {
  email: string;
  selected: boolean;
  __typename: string;
};
export const ContactListV3: React.FC<ContactListProps> = ({
  contacts,
  taskTagUsers,
  filterVal,
  alphabetize = false,
  onPress,
  headerSpace,
  footerSpace,
  isEditing,
  isInviting,
  loading,
  selected,
  shouldDisplayPhoneContactLabel,
  disabledUsersId,
  showMessageIcon = false,
  showEmptyStateNoSearch = false,
  onMessagePress,
  refreshing = false,
  onRefresh,
  hasNextPage = false,
  loadMore,
  avatarSize,
  onPressEmailAddress,
  groupList,
  onPressGroup,
  invitedList = [],
  alreadyInvitedUserIds = [],
  showLoadingSkeleton = false,
}) => {
  const { t } = useTranslation();

  const [sortedFilteredContacts, setSortedFilteredContacts] =
    useState<ContactType[]>(contacts);

  const { renderListHeader, renderListFooter } = useContactListSpacers({
    headerSpace,
    footerSpace,
    showEmptyStateNoSearch,
  });

  useEffect(() => {
    const filtered = filterVal
      ? contacts.filter(
          (contact) =>
            contact.displayName
              .toLowerCase()
              .includes(filterVal.toLowerCase()) ||
            (contact?.email ?? '')
              .toLowerCase()
              .includes(filterVal.toLowerCase())
        )
      : contacts;

    // Set both filtered and complete contact lists

    setSortedFilteredContacts(
      filtered.sort((a, b) =>
        a.displayName.toLowerCase().localeCompare(b.displayName.toLowerCase())
      )
    );
  }, [contacts, filterVal]);

  useEffect(() => {
    if (!alphabetize) return;
    // Only call when filteredContacts change, not on length alone
    if (sortedFilteredContacts.length > 0 || filterVal !== '') {
      getAlphabetizedSectionList();
    }
  }, [sortedFilteredContacts, filterVal]);

  const sortContacts = () => {
    const numsSpecials: ContactType[] = []; // numbers and ASCII before A
    const letters: ContactType[] = []; // letters
    const asterisks: ContactType[] = []; // ASCII after Z

    sortedFilteredContacts.forEach((s) => {
      const firstChar = s.displayName?.[0];
      if (!firstChar) return;

      const charCode = firstChar.charCodeAt(0);

      if (/[a-z]/i.test(firstChar)) {
        letters.push(s); // Letters A-Z or a-z
      } else if (charCode < 65) {
        numsSpecials.push(s); // Characters before 'A'
      } else {
        asterisks.push(s); // Characters after 'Z'
      }
    });

    // Helper function to sort by displayName's first character's char code
    const sortByCharCode = (arr: ContactType[]) =>
      arr.sort(
        (a, b) =>
          a.displayName[0].charCodeAt(0) - b.displayName[0].charCodeAt(0)
      );

    // Sort each category
    sortByCharCode(numsSpecials);
    letters.sort((a, b) =>
      a.displayName[0]
        .toUpperCase()
        .localeCompare(b.displayName[0].toUpperCase())
    );
    sortByCharCode(asterisks);

    return { letters, numsSpecials, asterisks };
  };

  const getAlphabetizedSectionList = () => {
    const tmpAlphabetizeList = ['#'];
    const { letters } = sortContacts();

    const formattedAlpha: (ContactType | string)[] = [];

    letters.forEach((obj) => {
      const key = obj.displayName.charAt(0).toUpperCase();

      // Check if the key is already added, if not, add it before the contact
      if (!formattedAlpha.includes(key)) {
        formattedAlpha.push(key);
      }
      formattedAlpha.push(obj);
    });

    // Add A-Z to the alphabetize list
    for (
      let charCode = 'A'.charCodeAt(0);
      charCode <= 'Z'.charCodeAt(0);
      charCode++
    ) {
      tmpAlphabetizeList.push(String.fromCharCode(charCode));
    }

    // Add asterisk section to the alphabetize list
    tmpAlphabetizeList.push('*');
  };

  if (loading)
    return showLoadingSkeleton ? (
      // ContactRowSkeleton has no horizontal padding of its own — other
      // screens that already use it wrap it in their own padded container.
      // ContactV3's real row applies paddingHorizontal='m' to itself, so
      // match it here rather than changing the shared composite.
      <Box paddingHorizontal='m'>
        <SkeletonList count={3} row={renderContactRowSkeleton} />
      </Box>
    ) : (
      <ActivityIndicatorLoading testID='loading-indicator' />
    );

  const renderContact = (
    item: ContactType,
    disabled?: boolean,
    alreadyInvited?: boolean
  ) => {
    // const isTaskTagUser = taskTagUserIds?.includes(item.id);
    return (
      <ContactV3
        key={item.id}
        toggleFriendRequestSuccess={() => {}}
        isTaskTagUser={false}
        contact={{ ...item, avatarSize: avatarSize }}
        filterVal={filterVal}
        onPressAvatar={onPress}
        onPress={() => {
          onPress(item);
        }}
        isEditing={isEditing}
        isSelected={selected?.some((i) => i === item.id) || item.isAssigned}
        isInviting={isInviting}
        shouldDisplayPhoneContactLabel={shouldDisplayPhoneContactLabel}
        disabled={disabled || item.isAssigned || alreadyInvited}
        alreadyInvited={alreadyInvited}
        showMessageIcon={showMessageIcon}
        onMessagePress={() => onMessagePress?.(item)}
      />
    );
  };
  const getXSuggestionEmail = () => {
    const vemail = getSuggestionEmail(filterVal ?? '');
    if (vemail === '') return undefined;
    if (
      taskTagUsers?.some(
        (ttu) => (ttu.email ?? '').toLowerCase() === vemail.toLowerCase()
      ) ||
      contacts?.some(
        (ttu) => (ttu.email ?? '').toLowerCase() === vemail.toLowerCase()
      )
    ) {
      return undefined;
    }
    return {
      email: vemail,
      selected: false,
      __typename: 'SuggestionEmailType',
    } as SuggestionEmailType;
  };

  const contactsAndUsersList = () => {
    const temp: any[] = [...(sortedFilteredContacts ?? [])];

    const sEmail = getXSuggestionEmail();

    if ((filterVal?.length ?? 0) > 0 && (sEmail?.email?.length ?? 0) > 0) {
      temp.unshift(sEmail);
    }
    groupList?.forEach((group) => {
      temp.push({
        id: group.id,
        name: group.name,
        __typename: 'Group',
      } as Group);

      temp.push(
        ...convertUsersToContacts(group.users ?? []).map((lu) => ({
          ...lu,
          isTaskTagUser: false,
        }))
      );
    });

    temp.push(t('shared:moreOnTaskTag'));
    temp.push(...(taskTagUsers ?? []));
    return temp;
  };

  if (
    ((filterVal ?? '').length > 0 && getXSuggestionEmail()) ||
    (taskTagUsers?.length ?? 0) > 0 ||
    (groupList?.length ?? 0) > 0
  ) {
    // section list

    return (
      <View style={{ flex: 1 }}>
        <Box mb='xs' marginHorizontal='m'>
          <Text variant='labelEmphasized' color='textPrimary'>
            Suggested
          </Text>
        </Box>

        <FlashList
          style={{ marginTop: theme.spacing.xs }}
          showsVerticalScrollIndicator={false}
          keyboardDismissMode='on-drag'
          ListFooterComponent={MarginBottomListFooter}
          data={contactsAndUsersList()}
          renderItem={({ item }) => {
            if (typeof item === 'string') {
              return (
                <Box mt='xs' marginHorizontal='m' py='xs'>
                  <Text variant='body1' color='textPrimary'>
                    {item}
                  </Text>
                </Box>
              );
            } else if (item.__typename === 'SuggestionEmailType') {
              const eitem = item as SuggestionEmailType;
              const isInvited = invitedList.includes(eitem.email);
              return (
                <TouchableOpacity
                  disabled={isInvited}
                  onPress={() => {
                    onPressEmailAddress?.(eitem.email);
                  }}>
                  <WebHoverBox
                    flexDirection='row'
                    alignItems='center'
                    py='xs'
                    px='m'>
                    <EmailAvatar width={40} />
                    <Box marginLeft='xs' flex={1}>
                      <Text
                        variant='body1'
                        color='textPrimary'
                        numberOfLines={1}
                        ellipsizeMode='tail'>
                        <Highlighter
                          autoEscape
                          highlightStyle={
                            Platform.OS === 'web'
                              ? {
                                  backgroundColor: theme.colors.yellowBright,
                                }
                              : {
                                  color: theme.colors.greenSecondary,
                                  backgroundColor: theme.colors.background,
                                }
                          }
                          searchWords={filterVal?.split(' ') || ''}
                          textToHighlight={eitem.email}
                        />
                      </Text>
                      {isInvited && (
                        <Text
                          variant='webBodyHintSubline'
                          color='greenSecondary'
                          numberOfLines={1}
                          ellipsizeMode='tail'>
                          {t('models:chat.alreadyInvited')}
                        </Text>
                      )}
                    </Box>
                    <Box justifyContent='center' marginLeft='m' mr='xs'>
                      <RadioButton
                        onPress={() => {
                          onPressEmailAddress?.(eitem.email);
                        }}
                        isSelected={
                          selected?.some((t) => t === eitem.email) ?? false
                        }
                        iconVariant='m'
                        disabled={isInvited}
                      />
                    </Box>
                    {/*<RadioButton></RadioButton>*/}
                  </WebHoverBox>
                </TouchableOpacity>
              );
            } else if (item.__typename === 'Group') {
              const g = groupList?.filter((g1) => g1.id === item.id) ?? [];

              const userIds = (g[0].users ?? []).map((gu) => gu.id);
              let isSelected = true;
              userIds.forEach((ui) => {
                if (!selected?.includes(ui)) {
                  isSelected = false;
                }
              });
              let disable = false;
              userIds.forEach((ui) => {
                if (disabledUsersId?.includes(ui)) {
                  disable = true;
                }
              });
              return (
                <Box mt='xs' py='xs' ml='m' mr='l' flexDirection='row'>
                  <Text
                    flex={1}
                    flexShrink={1}
                    numberOfLines={1}
                    ellipsizeMode='tail'
                    variant='body1'
                    color='textPrimary'
                    marginRight='m'>
                    <Highlighter
                      highlightStyle={
                        Platform.OS === 'web'
                          ? {
                              backgroundColor: theme.colors.yellowBright,
                            }
                          : {
                              color: theme.colors.greenSecondary,
                              backgroundColor: theme.colors.background,
                            }
                      }
                      autoEscape
                      searchWords={filterVal?.split(' ') || ''}
                      textToHighlight={item.name}
                    />
                  </Text>
                  <RadioButton
                    onPress={() => {
                      const g = groupList?.filter((g1) => g1.id === item.id);
                      if ((g?.length ?? 0) > 0)
                        onPressGroup?.(g[0], !isSelected);
                    }}
                    isSelected={isSelected}
                    iconVariant='m'
                    disabled={disable}
                  />
                </Box>
              );
            } else {
              return renderContact(
                item,
                disabledUsersId?.includes(item.id),
                alreadyInvitedUserIds?.includes(item.id)
              );
            }
          }}
          getItemType={(item) => {
            // To achieve better performance, specify the type based on the item
            return typeof item === 'string' ? 'sectionHeader' : 'row';
          }}
          estimatedItemSize={ESTIMATED_CONTACT_ITEM_SIZE}
          drawDistance={CONTACT_LIST_DRAW_DISTANCE}
          refreshing={loading}
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
          }
          onEndReached={hasNextPage ? loadMore : undefined}
        />
      </View>
    );
  }

  return (
    <FlashList
      style={{ marginTop: theme.spacing.xs }}
      showsVerticalScrollIndicator={false}
      extraData={{ selected, contacts, disabledUsersId }}
      estimatedItemSize={ESTIMATED_CONTACT_ITEM_SIZE}
      drawDistance={CONTACT_LIST_DRAW_DISTANCE}
      keyboardDismissMode='on-drag'
      data={sortedFilteredContacts}
      renderItem={({ item }) =>
        renderContact(item, disabledUsersId?.includes(item.id))
      }
      ListHeaderComponent={renderListHeader}
      ListFooterComponent={renderListFooter}
      ListEmptyComponent={
        showEmptyStateNoSearch ? (
          <Box style={{ marginTop: -theme.spacing.listFooter / 2 }}>
            <EmptyStateNoSearch showSmall />
          </Box>
        ) : undefined
      }
    />
  );
};
